import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Navigation } from "lucide-react";
import { fadeUp, stagger } from "./motion";
import { LOCATIONS } from "../../data/site";
import { trackEvent } from "../../lib/analytics";

export const Locations = () => {
  const [active, setActive] = useState(0);
  const loc = LOCATIONS[active];

  return (
    <section id="locations" data-testid="locations-section" className="relative bg-[#FFE9F1]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <motion.span variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="eyebrow text-[#12B99B]">Find Us</motion.span>
            <motion.h2 variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="font-display mt-3 text-[clamp(2rem,5vw,3.75rem)] leading-[1.02] text-[#2A1620]">
              Six California <span className="font-script text-[#F63C8B]">Locations</span>
            </motion.h2>
          </div>
          <p className="text-[#8A7480] text-sm max-w-xs">Six neighborhood shops across California — from Lathrop to Merced.</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-5">
          {/* list */}
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="lg:col-span-5 flex flex-col gap-3">
            {LOCATIONS.map((l, i) => (
              <motion.button key={l.city} variants={fadeUp} data-testid={`location-tab-${i}`}
                onClick={() => setActive(i)}
                className={`text-left rounded-2xl px-6 py-5 transition-all border ${
                  active === i
                    ? "bg-[#F63C8B] border-[#F63C8B] text-white shadow-[0_18px_36px_-18px_rgba(246,60,139,0.8)]"
                    : "bg-white border-[#F8DCE7] hover:border-[#F9A8CB]"
                }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`font-display text-xl sm:text-2xl leading-none ${active === i ? "text-white" : "text-[#2A1620]"}`}>{l.city}</h3>
                    <p className={`mt-2 text-sm ${active === i ? "text-white/90" : "text-[#8A7480]"}`}>{l.address}</p>
                  </div>
                  <span className={`font-display text-lg ${active === i ? "text-white/70" : "text-[#F4C9DC]"}`}>{String(i + 1).padStart(2, "0")}</span>
                </div>
              </motion.button>
            ))}
          </motion.div>

          {/* detail + map */}
          <div className="lg:col-span-7 card overflow-hidden flex flex-col">
            <div className="relative h-64 sm:h-80 w-full overflow-hidden">
              <iframe key={active} title={`Map of ${loc.city}`} data-testid="location-map"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(loc.maps + ", " + loc.address)}&z=13&output=embed`}
                className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
            <div className="p-6 sm:p-8 flex-1">
              <h3 className="font-display text-2xl text-[#2A1620]">{loc.city}, {loc.state}</h3>
              <ul className="mt-5 space-y-3.5 text-[#3A2A32]">
                <li className="flex items-start gap-3"><MapPin size={18} className="text-[#F63C8B] mt-0.5 shrink-0" /> {loc.address}</li>
                <li className="flex items-center gap-3"><Phone size={18} className="text-[#F63C8B] shrink-0" /> <a href={`tel:${loc.phone.replace(/[^0-9]/g, "")}`} className="link-underline">{loc.phone}</a></li>
                <li className="flex items-center gap-3"><Clock size={18} className="text-[#F63C8B] shrink-0" /> {loc.hours}</li>
              </ul>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(loc.address)}`}
                  target="_blank" rel="noopener noreferrer" data-testid={`location-directions-${active}`}
                  className="btn-mint inline-flex items-center justify-center gap-2 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.12em]">
                  <Navigation size={15} /> Directions
                </a>
                <a href={loc.order} target="_blank" rel="noopener noreferrer"
                  onClick={() => trackEvent("location_selected", { location: loc.city })}
                  data-testid={`location-order-${active}`}
                  className="btn-primary inline-flex items-center justify-center px-6 py-3.5 text-xs font-bold uppercase tracking-[0.12em]">
                  Order Online
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
